"use client";

import { useEffect, useState } from "react";

export type ViewportRange = "narrow" | "regular" | "wide";

const REGULAR_MIN = "768px";
const WIDE_MIN = "1400px";

function resolveRange(): ViewportRange {
  if (typeof window === "undefined") return "regular";
  if (window.matchMedia(`(min-width: ${WIDE_MIN})`).matches) return "wide";
  if (window.matchMedia(`(min-width: ${REGULAR_MIN})`).matches) return "regular";
  return "narrow";
}

/**
 * Returns the current viewport range — "narrow" / "regular" / "wide".
 *
 * Layout-level code should branch on viewport ranges rather than raw breakpoints
 * because the range encodes intent (column capacity) rather than pixel width.
 * Component-internal responsive decisions (e.g. `md:hidden`) keep using Tailwind
 * breakpoints; the range vocabulary is for layout-level decisions only.
 */
export function useViewportRange(): ViewportRange {
  const [range, setRange] = useState<ViewportRange>(resolveRange);

  useEffect(() => {
    const wideMedia = window.matchMedia(`(min-width: ${WIDE_MIN})`);
    const regularMedia = window.matchMedia(`(min-width: ${REGULAR_MIN})`);
    const update = () => setRange(resolveRange());

    wideMedia.addEventListener("change", update);
    regularMedia.addEventListener("change", update);
    return () => {
      wideMedia.removeEventListener("change", update);
      regularMedia.removeEventListener("change", update);
    };
  }, []);

  return range;
}
